import { Fragment } from 'react';

import { CLASSIFICACAO } from '@/lib/classificacao';
import { cx } from '@/lib/cx';
import { type RegraClassificacao } from '@fiscoai/contracts';

import { CONDICAO, condicoesPreenchidas, resumirValores, type TipoCondicao } from './textos';

/** Um valor da condição como "peça" da frase: código em fonte de dados, fundo afundado. */
function Valor({ texto, mono, ativa }: { texto: string; mono: boolean; ativa: boolean }) {
  return (
    <span
      className={cx(
        'rounded-mini bg-superficie-afundada px-1 py-px',
        ativa ? 'text-texto' : 'text-texto-suave',
        mono && 'font-mono text-sm tabular',
      )}
    >
      {texto}
    </span>
  );
}

function Condicao(p: { tipo: TipoCondicao; valores: readonly string[]; ativa: boolean }) {
  const { tipo, valores } = p;
  const meta = CONDICAO[tipo];
  const { visiveis, restantes } = resumirValores(valores, meta.formatar);
  return (
    <>
      {meta.frase}{' '}
      {visiveis.map((v, i) => (
        <Fragment key={v}>
          {i > 0 && (i === visiveis.length - 1 && restantes === 0 ? ' ou ' : ', ')}
          <Valor texto={v} mono={meta.mono} ativa={p.ativa} />
        </Fragment>
      ))}
      {restantes > 0 && <span className="text-texto-fraco"> … (+{restantes})</span>}
    </>
  );
}

/**
 * A regra escrita como frase: "Quando o NCM começar com 2710.12.59, 2207.10 … (+4) classificar
 * como Combustível no centro de custo FROTA". Desligada, a frase inteira fica em texto suave.
 */
export function FraseRegra({ regra }: { regra: RegraClassificacao }) {
  const tipos = condicoesPreenchidas(regra.condicoes);
  return (
    <p className={cx('leading-relaxed', regra.ativa ? 'text-texto-suave' : 'text-texto-fraco')}>
      Quando{' '}
      {tipos.map((t, i) => (
        <Fragment key={t}>
          {i > 0 && ' e '}
          <Condicao tipo={t} valores={regra.condicoes[t]} ativa={regra.ativa} />
        </Fragment>
      ))}{' '}
      classificar como{' '}
      <strong className={cx('font-semibold', regra.ativa ? 'text-marca' : 'text-texto-suave')}>
        {regra.personalizada?.nome ?? CLASSIFICACAO[regra.classificacao]}
      </strong>
      {regra.centroCusto && (
        <>
          {' '}
          no centro de custo{' '}
          <strong className={cx('font-semibold', regra.ativa ? 'text-texto' : 'text-texto-suave')}>
            {regra.centroCusto.codigo} – {regra.centroCusto.nome}
          </strong>
        </>
      )}
    </p>
  );
}
